import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, FolderOpen, LogOut, Trash2, Edit, Eye, Bell, Clock, CheckCircle2, MessageSquare } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    password: "",
    description: "",
  });

  const token = localStorage.getItem("adminToken");

  const fetchData = useCallback(async () => {
    try {
      const [catRes, statsRes] = await Promise.all([
        axios.get(`${API}/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCategories(catRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/webo0os");
      }
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/webo0os");
      return;
    }
    fetchData();
  }, [token, navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUsername");
    navigate("/webo0os");
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setForm({
      name: category.name,
      password: category.password,
      description: category.description,
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingCategory(null);
    setForm({ name: "", password: "", description: "" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.password.trim()) {
      toast.error("Nombre y contraseña son requeridos");
      return;
    }

    setSubmitting(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/categories`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Categoría creada");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Categoría eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="admin-dashboard">
      {/* Header */}
      <header className="mb-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider text-foreground mb-2">
              CONTROL CENTER
            </h1>
            <p className="text-muted-foreground text-sm tracking-wide">
              GESTIÓN DE PROYECTOS
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={openNewDialog}
                  className="bg-primary hover:bg-primary/90 uppercase tracking-wider btn-glow"
                  data-testid="new-category-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Categoría
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-xl uppercase tracking-wider">
                    {editingCategory ? "Editar Categoría" : "Nueva Categoría"}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Nombre del Proyecto *
                    </label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ej: Sitio Web Empresa X"
                      className="bg-input border-border"
                      data-testid="category-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Contraseña de Acceso *
                    </label>
                    <Input
                      value={form.password}
                      onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Contraseña única para este proyecto"
                      className="bg-input border-border font-mono"
                      data-testid="category-password-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Esta contraseña permitirá al cliente acceder solo a este proyecto
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Descripción
                    </label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Descripción opcional del proyecto..."
                      className="bg-input border-border"
                      data-testid="category-description-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-primary hover:bg-primary/90 uppercase tracking-wider"
                    data-testid="save-category-button"
                  >
                    {submitting ? "Guardando..." : (editingCategory ? "Actualizar" : "Crear Categoría")}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-border"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Total Proyectos
            </p>
            <p className="text-2xl font-bold mono">{stats.total_categories || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Total Correcciones
            </p>
            <p className="text-2xl font-bold mono">{stats.total_corrections || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Pendientes
            </p>
            <p className="text-2xl font-bold mono text-yellow-400">{stats.pending_corrections || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 relative">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Nuevas
            </p>
            <p className="text-2xl font-bold mono text-primary">{stats.new_corrections || 0}</p>
            {stats.new_corrections > 0 && (
              <Bell className="absolute top-4 right-4 w-5 h-5 text-primary animate-pulse" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Categories Grid */}
      {categories.length === 0 ? (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-md bg-card border border-border mb-4">
            <FolderOpen className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Sin proyectos</h3>
          <p className="text-muted-foreground text-sm">
            Crea tu primera categoría para comenzar a recibir correcciones
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category, index) => (
            <Card 
              key={category.id} 
              className="bg-card border-border card-hover animate-fade-in relative"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`category-card-${category.id}`}
            >
              {category.new_corrections_count > 0 && (
                <div className="new-indicator" data-testid={`new-badge-${category.id}`}>
                  {category.new_corrections_count}
                </div>
              )}
              
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-primary" />
                  {category.name}
                </CardTitle>
              </CardHeader>
              
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {category.description || "Sin descripción"}
                </p>
                
                {/* Counters: Pendientes, Retroalimentación, Completadas */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-orange-900/20 border border-orange-900/30 rounded-sm p-2 text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Clock className="w-3 h-3 text-orange-400" />
                    </div>
                    <p className="text-xl font-bold text-orange-400 mono">{category.pending_count || 0}</p>
                    <span className="text-[10px] uppercase tracking-wider text-orange-400">Pendientes</span>
                  </div>
                  <div className="bg-purple-900/20 border border-purple-900/30 rounded-sm p-2 text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <MessageSquare className="w-3 h-3 text-purple-400" />
                    </div>
                    <p className="text-xl font-bold text-purple-400 mono">{category.feedback_count || 0}</p>
                    <span className="text-[10px] uppercase tracking-wider text-purple-400">Retroalim.</span>
                  </div>
                  <div className="bg-green-900/20 border border-green-900/30 rounded-sm p-2 text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <CheckCircle2 className="w-3 h-3 text-green-400" />
                    </div>
                    <p className="text-xl font-bold text-green-400 mono">{category.completed_count || 0}</p>
                    <span className="text-[10px] uppercase tracking-wider text-green-400">Completadas</span>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-sm p-3 mb-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Contraseña de Acceso
                  </p>
                  <p className="font-mono text-sm">{category.password}</p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => navigate(`/webo0os/category/${category.id}`)}
                    className="flex-1"
                    data-testid={`view-category-${category.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Ver
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(category)}
                    className="border-border"
                    data-testid={`edit-category-${category.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-border text-destructive hover:bg-destructive/10"
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-card border-border">
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta acción eliminará la categoría "{category.name}" y todas sus correcciones asociadas. Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(category.id)}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
